import { Heading, ScrollList, theme } from '@hhgtech/hhg-components'
import Link from 'next/link'
import { useIntl } from 'react-intl'
import Skeleton from 'react-loading-skeleton'

import { HealthToolCard } from 'components/molecules/healthToolCard'
import { domainLocales } from 'domainLocales'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { assetImagePath, basePath } from 'interfaces/constants'
import { HealthTool } from 'interfaces/types'

import {
  StyledFooterGroup,
  StyledScrollListContainer,
  StyledSeeAllButton,
  StyleRecommendedTopics,
} from './index.styled'

export type Props = {
  healthToolsLoading: boolean
  healthTools?: HealthTool[]
  isMobile?: boolean
}

const HealthToolsCard = ({
  isMobile,
  healthTools = [],
  healthToolsLoading,
}: Props) => {
  const { formatMessage: f } = useIntl()
  const { locale } = useRouterExtend()
  const allToolsUrl = '/health-tools'

  const CategoriesSkeleton = () => (
    <ScrollList row={2} column={3} mode="snapped" type="separated">
      <Skeleton height={125} width={100} />
      <Skeleton height={125} width={100} />
      <Skeleton height={125} width={100} />
      <Skeleton height={125} width={100} />
      <Skeleton height={125} width={100} />
      <Skeleton height={125} width={100} />
    </ScrollList>
  )

  if (healthToolsLoading) {
    return <CategoriesSkeleton />
  }

  return (
    <StyleRecommendedTopics
      style={{
        marginTop: 24,
        padding: 24,
        border: '1px solid #E4E8EC',
        borderRadius: 4,
      }}
    >
      <Heading
        tag="h5"
        color={theme.colors.gray800}
        style={{ textAlign: 'center' }}
      >
        {f({
          id: 'home.healthTools',
          defaultMessage: 'Other Health Tools',
        })}
      </Heading>
      <StyledScrollListContainer
        style={
          isMobile
            ? {
                padding: '16px 0',
              }
            : {
                padding: '20px 0',
                paddingBottom: 0,
              }
        }
      >
        {healthToolsLoading ? (
          <CategoriesSkeleton />
        ) : (
          healthTools.length > 0 && (
            <ScrollList
              isDotsShown={false}
              row={3}
              column={3}
              mode="snapped"
              type="separated"
            >
              {healthTools.map((tool, index) => (
                <HealthToolCard
                  key={index}
                  cardInfo={{
                    image: tool.icon || `${assetImagePath}/avatar.jpg`,
                    title: tool.title,
                    url: tool.link,
                  }}
                />
              ))}
            </ScrollList>
          )
        )}
      </StyledScrollListContainer>
      <StyledFooterGroup>
        <Link prefetch={false} href={allToolsUrl || ''} legacyBehavior>
          <StyledSeeAllButton
            data-event-category="Other Health Tools"
            data-event-action="Click See All"
            data-event-label={`https://${domainLocales[locale]}${basePath}/health-tools`}
          >
            <Heading tag="h5" color={theme.colors.primaryBase}>
              {f({
                id: 'home.seeAll',
                defaultMessage: 'See All',
              })}
            </Heading>
          </StyledSeeAllButton>
        </Link>
      </StyledFooterGroup>
    </StyleRecommendedTopics>
  )
}

export { HealthToolsCard }
